import React, { useEffect, useState } from "react";

interface StartChatModalProps {
  name: string;
  isOpen?: boolean;
  minutes?: number;
  isStarting: boolean;
  onClose?: () => void;
  onStart?: () => void;
}

const StartChatModal: React.FC<StartChatModalProps> = ({
  name,
  isOpen = false,
  minutes,
  isStarting = false,
  onClose,
  onStart,
}) => {

  const [hasCredits, setHasCredits] = useState<boolean>(false)
  const [consent, setConsent] = useState<boolean>(false)

  useEffect(() => {

    setHasCredits(minutes !== undefined && minutes >= 1);

  }, [minutes]);

  const handleLoadAccount = () => {
    onClose?.();

    if (typeof window === "undefined") return;

    const sectionId = "planos";

    if (window.location.pathname !== "/") {
      localStorage.setItem("pendingScrollSection", sectionId);
      window.location.href = "/";
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const offsetTop = element.getBoundingClientRect().top + window.scrollY - headerOffset;
      window.scrollTo({ top: offsetTop < 0 ? 0 : offsetTop, behavior: "smooth" });
    }
  };
  


  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="start-chat-title"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 w-full max-w-md rounded-2xl border border-[rgba(148,163,184,0.35)] shadow-[0_22px_50px_rgba(15,23,42,0.85)] bg-[radial-gradient(circle_at_top,#111827,#020617_60%)] text-[#f9fafb]">
        <div className="p-6">
          <h2 id="start-chat-title" className="mt-0 mb-2 text-xl font-bold">
            {hasCredits ? "Pronto para iniciar chat?" : "Parece que nao tem créditos suficientes"}
          </h2>
          <p className="text-[0.95rem] text-[#cbd5f5] mb-4">
            {hasCredits ? 
            `Vai iniciar uma conversa privada com ${name}. Antes de começar, leia
            as instruções rápidas abaixo.` : ""}
          </p>

          <div className="mb-4">
            <h3 className="text-sm font-semibold text-[#e5e7eb] mb-2">
              Saldo de créditos
            </h3>
            <div className="flex items-center gap-3">
              { hasCredits ? (
                <>
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-sm bg-emerald-500/15 text-emerald-300 border border-emerald-400/20">
                    {typeof minutes === "number"
                      ? `${minutes} créditos disponíveis`
                      : "— créditos disponíveis"}
                  </span>
                  <span className="text-xs text-[#94a3b8]">
                    Os créditos serão debitados ao segundo.
                  </span>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-sm bg-red-500/15 text-red-300 border border-red-400/20">
                    {typeof minutes === "number"
                      ? `${minutes} créditos disponíveis`
                      : "— créditos disponíveis"}
                  </span>
                  <span className="text-xs text-[#94a3b8]">
                    Créditos insuficientes.
                  </span>
                </>
              )}
            </div>
          </div>

          <ul className="list-disc pl-5 space-y-1 mb-5 text-[#cbd5f5] text-[0.95rem]">
            <li>O chat é privado e confidencial.</li>
            <li>Envie apenas uma mensagem de cada vez.</li>
            <li>Mantenha linguagem respeitosa e objetiva.</li>
            <li>Pode terminar a qualquer momento.</li>
          </ul>

          {hasCredits && (
            <label className="flex items-start gap-2.5 cursor-pointer mb-5">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-[#334155] bg-[#0f172a] text-[#8b5cf6] focus:ring-2 focus:ring-[rgba(139,92,246,0.2)] cursor-pointer"
              />
              <span className="text-[0.85rem] text-[#cbd5e1] leading-tight">
                Compreendo e aceito que irei interagir com um sistema de Inteligência Artificial e que o serviço destina-se exclusivamente a fins de entretenimento.
              </span>
            </label>
          )}

          <div className="flex gap-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center rounded-full px-4 py-2 text-[0.95rem] font-medium border border-[rgba(249,250,251,0.5)] bg-transparent text-[#f9fafb] hover:bg-[rgba(15,23,42,0.9)] transition-colors"
              >
                Cancelar
              </button>
            )}
            {hasCredits ? (
              <button
                type="button"
                disabled={isStarting || !consent}
                onClick={onStart}
                className="flex-1 inline-flex items-center justify-center rounded-full px-4 py-2 text-[0.95rem] font-medium bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-[#f9fafb] shadow-[0_8px_20px_rgba(15,23,42,0.6)] hover:translate-y-[-1px] hover:brightness-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:brightness-100"
              >
                {isStarting ? "Iniciando..." : "Iniciar Chat"}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleLoadAccount}
                className="flex-1 inline-flex items-center justify-center rounded-full px-20 py-2 text-[0.95rem] font-medium bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-[#f9fafb] shadow-[0_8px_20px_rgba(15,23,42,0.6)] hover:translate-y-[-1px] hover:brightness-105 transition-all"
              >
                Carregar conta
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartChatModal;
